import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type {
  AppConfig,
  BillingMode,
  FeatureCapabilities,
  UserProfile,
} from "@/api/types";
import { useAuth } from "@/features/auth/AuthContext";
import { canShowCommercialUi, isExperimentalBilling } from "./capabilities";

interface ConfigContextValue {
  config: AppConfig | null;
  user: UserProfile | null;
  billingMode: BillingMode;
  capabilities: FeatureCapabilities;
  experimentalAccess: boolean;
  monetizationEnabled: boolean;
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
  updateUser: (user: UserProfile) => void;
}

const ConfigContext = createContext<ConfigContextValue | null>(null);

const safeCapabilities: FeatureCapabilities = {
  summaries: false,
  follow_through: false,
  audio: false,
  billing_management: false,
};

export function ConfigProvider({ children }: { children: ReactNode }) {
  const { user: authUser, status } = useAuth();
  const [config, setConfig] = useState<AppConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [profileOverride, setProfileOverride] = useState<UserProfile | null>(null);

  const user = profileOverride ?? authUser;

  const reload = useCallback(async () => {
    // The current web contract exposes profile, workspaces, meetings and
    // sessions. It does not expose the legacy /api/v1/config or /profile
    // endpoints, so configuration is intentionally derived locally.
    setError(null);
    setConfig(null);
    if (status !== "authenticated") setProfileOverride(null);
    setLoading(status === "loading");
  }, [status]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    if (status === "anonymous") setProfileOverride(null);
  }, [status]);

  const updateUser = useCallback((next: UserProfile) => {
    setProfileOverride(next);
  }, []);

  const billingMode = config?.billing_mode ?? "bypass";
  const userCommercialEntitled = user?.commercial_entitled;
  const capabilities = useMemo<FeatureCapabilities>(() => config
    ? {
        // Summary is available in the current product surface. Agent/audio
        // capabilities are introduced by their own server contracts.
        summaries: true,
        follow_through: false,
        audio: false,
        billing_management: canShowCommercialUi(config, userCommercialEntitled),
      }
    : safeCapabilities, [config, userCommercialEntitled]);
  const experimentalAccess = isExperimentalBilling(billingMode);
  const monetizationEnabled = Boolean(
    config !== null &&
    canShowCommercialUi(config, userCommercialEntitled) &&
    user?.monetization_enabled,
  );

  const value = useMemo(
    () => ({
      config,
      user,
      billingMode,
      capabilities,
      experimentalAccess,
      monetizationEnabled,
      loading,
      error,
      reload,
      updateUser,
    }),
    [
      config,
      user,
      billingMode,
      capabilities,
      experimentalAccess,
      monetizationEnabled,
      loading,
      error,
      reload,
      updateUser,
    ],
  );

  return <ConfigContext.Provider value={value}>{children}</ConfigContext.Provider>;
}

export function useConfig(): ConfigContextValue {
  const ctx = useContext(ConfigContext);
  if (!ctx) throw new Error("useConfig must be used within ConfigProvider");
  return ctx;
}

export function useMonetizationEnabled(): boolean {
  return useConfig().monetizationEnabled;
}
